/**
 * Usage analytics time series (GET …/aes/analytics) — backed by the
 * margarita-worker datalake, NOT the gateway's in-memory /usagez buckets.
 * Goes through the same @mgmt control plane proxy (serviceApiV3) as the rest
 * of the AES ops data layer, so the user's JWT authenticates and the admin key
 * is injected server-side.
 */
import { useQuery } from '@tanstack/react-query';
import { aesApi, aesPath, aesKeys } from './_shared';

export type AesAnalyticsMetric = 'keys_issued' | 'sessions_active' | 'unique_ips';

// Display granularity the /analytics endpoint accepts. 1h/day are time_bucket
// rollups of the raw 5m rows (the datalake stores counters only at 5m); the
// backend coerces unknown values to 5m and forces unique_ips to day.
export type AesAnalyticsGranularity = '5m' | '1h' | 'day';

export interface AesAnalyticsPoint {
  timestamp: string; // RFC3339
  value: number;
}

export interface AesAnalyticsParams {
  metric: AesAnalyticsMetric;
  granularity?: AesAnalyticsGranularity;
  from?: string;
  to?: string;
  group_by?: 'stream';
}

export interface AesAnalyticsSeries {
  metric: string;
  granularity: string;
  aggregation: string;
  series: AesAnalyticsPoint[];
  by_dimension?: Record<string, AesAnalyticsPoint[]>;
}

export function useAesAnalytics(serviceId: string | undefined, params: AesAnalyticsParams) {
  return useQuery<AesAnalyticsSeries>({
    queryKey: aesKeys.analytics(serviceId ?? '', params as unknown as Record<string, unknown>),
    enabled: !!serviceId,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
    // The /analytics endpoint is a real @mgmt handler (response.OK), so the body
    // is the SuccessResponse envelope { status, data } — unwrap `.data.data`.
    // (The proxy endpoints /usagez and /sessions forward the gateway body verbatim
    // with no envelope, which is why those hooks read `.data` directly.)
    queryFn: async () => {
      const res = await aesApi.get<{ data: AesAnalyticsSeries }>(
        aesPath(serviceId!, '/analytics'),
        { params },
      );
      return res.data.data;
    },
  });
}
